import { initContract } from '@ts-rest/core';
import { z } from 'zod';
////////////////////////////////////////////////////////////////////////////////
// NewClaimDetails
////////////////////////////////////////////////////////////////////////////////
export const NewClaimDetails = z.strictObject({
    claimDate: z.string().nullable(),
    isClaimDatePreliminary: z.boolean(),
    contractNumber: z.string().min(1).nullable(),
    policyHolderName: z.string().min(1).nullable(),
    claimLocation: z.string().min(1).nullable(),
    customerObjectNumber: z.string().min(1).nullable(),
    customerReferenceNumber: z.string().min(1).nullable(),
    contactPerson: z.string().min(1).nullable(),
    contactPhone: z.string().min(1).nullable(),
    contactEmail: z.string().email().nullable(),
    claimType: z.string().min(1).nullable(),
    claimCause: z.string().min(1).nullable(),
    claimDescription: z.string().min(1).nullable(),
    affectedBuildingParts: z.string().min(1).nullable(),
    floorCoverDamage: z.enum(['Nein', 'Ja', 'Ja (Mietereigentum)']).nullable(),
    expectedClaimAmountEur: z.number().nonnegative().nullable(),
    buildingUnitsTotal: z.number().int().nonnegative().nullable(),
    buildingUnitsVacant: z.number().int().min(0).nullable(),
    authoritiesPresent: z.enum(['Polizei', 'Feuerwehr']).array(),
    policeReportFiled: z.enum(['Nein', 'Ja']).nullable(),
    policeProtocolNumber: z.string().min(1).nullable(),
    policeStation: z.string().min(1).nullable(),
    /** Whether the policy holder may deduct input tax for the insured object; null when the filer left it open. */
    inputTaxDeduction: z.enum(['Nein', 'Ja, vollständig', 'Ja, teilweise']).nullable(),
    /** The deductible share in percent, only with a partial deduction and only when the filer knows it. */
    inputTaxDeductionSharePercent: z.number().gt(0).lt(100).nullable(),
    /** Where a payout goes: the account the premium is paid from, or the IBAN the filer names; null when left open. */
    payoutAccount: z.enum(['Konto des Beitragszahlers', 'Anderes Konto']).nullable(),
    /** The IBAN of the other account, only with `Anderes Konto`. */
    payoutIban: z.string().min(1).nullable(),
    /** The holder of the other account, only with `Anderes Konto` and only when the filer names one. */
    payoutAccountHolder: z.string().min(1).nullable(),
    affectedParties: z
        .strictObject({
        role: z.enum(['Mieter', 'Eigentümer', 'Sonstiger']),
        name: z.string().min(1).nullable(),
        phone: z.string().min(1).nullable(),
        email: z.string().email().nullable(),
        address: z.string().min(1).nullable(),
        buildingUnit: z.string().min(1).nullable(),
        householdInsuranceInsurer: z.string().min(1).nullable(),
        householdInsuranceContractNumber: z.string().min(1).nullable(),
    })
        .array(),
    causingParties: z
        .strictObject({
        role: z.enum(['Mieter', 'Eigentümer', 'Sonstiger']),
        name: z.string().min(1).nullable(),
        phone: z.string().min(1).nullable(),
        email: z.string().email().nullable(),
        address: z.string().min(1).nullable(),
        buildingUnit: z.string().min(1).nullable(),
        liabilityInsuranceInsurer: z.string().min(1).nullable(),
        liabilityInsuranceContractNumber: z.string().min(1).nullable(),
    })
        .array(),
    message: z.string().min(1).nullable(),
    files: z
        .strictObject({
        name: z.string().min(1),
        mimetype: z.string().min(1),
        hash: z.string().min(1),
    })
        .array(),
});
////////////////////////////////////////////////////////////////////////////////
// getFormSuggestions
////////////////////////////////////////////////////////////////////////////////
export const GetFormSuggestionsResponse = z.strictObject({
    customerObjectNumbers: z.array(z.string()),
    customerReferenceNumbers: z.array(z.string()),
    contactPersons: z.array(z.string()),
    contactPhones: z.array(z.string()),
    contactEmails: z.array(z.string()),
});
////////////////////////////////////////////////////////////////////////////////
// getContractNumberProposals
////////////////////////////////////////////////////////////////////////////////
export const GetContractNumberProposalsRequest = z.strictObject({
    claimDate: z.string().min(1).optional(),
});
export const GetContractNumberProposalsResponse = z.strictObject({
    contracts: z.array(z.strictObject({
        contractNumber: z.string(),
        policyHolderName: z.string().nullable(),
    })),
});
////////////////////////////////////////////////////////////////////////////////
// getClaimLocationProposals
////////////////////////////////////////////////////////////////////////////////
export const GetClaimLocationProposalsRequest = z.strictObject({
    claimDate: z.string().min(1).optional(),
    contractNumber: z.string().min(1).optional(),
});
export const GetClaimLocationProposalsResponse = z.strictObject({
    claimLocations: z.array(z.string()),
});
////////////////////////////////////////////////////////////////////////////////
// getClaimTypeProposals
////////////////////////////////////////////////////////////////////////////////
export const GetClaimTypeProposalsRequest = z.strictObject({
    contractNumber: z.string().min(1).optional(),
});
export const GetClaimTypeProposalsResponse = z.strictObject({
    claimTypes: z.array(z.string()),
});
////////////////////////////////////////////////////////////////////////////////
// getClaimCauseProposals
////////////////////////////////////////////////////////////////////////////////
export const GetClaimCauseProposalsRequest = z.strictObject({
    contractNumber: z.string().min(1).optional(),
    claimType: z.string().min(1).optional(),
});
export const GetClaimCauseProposalsResponse = z.strictObject({
    claimCauses: z.array(z.string()),
});
////////////////////////////////////////////////////////////////////////////////
// contract
////////////////////////////////////////////////////////////////////////////////
const c = initContract();
export const newClaimContract = c.router({
    createNewClaim: {
        method: 'POST',
        path: '/create-new-claim',
        body: z.strictObject({}),
        responses: {
            200: z.strictObject({
                id: z.string(),
            }),
        },
    },
    getNewClaim: {
        method: 'GET',
        path: '/get-new-claim/:id',
        pathParams: z.strictObject({
            id: z.string(),
        }),
        responses: {
            200: NewClaimDetails.extend({
                status: z.enum(['draft', 'filing', 'filed', 'failed']),
            }),
        },
    },
    updateNewClaim: {
        method: 'PUT',
        path: '/update-new-claim/:id',
        pathParams: z.strictObject({
            id: z.string(),
        }),
        body: NewClaimDetails,
        responses: {
            200: z.strictObject({
                success: z.boolean(),
            }),
        },
    },
    getFileUploadUrl: {
        method: 'POST',
        path: '/get-file-upload-url/:id',
        pathParams: z.strictObject({
            id: z.string(),
        }),
        body: z.strictObject({
            fileHash: z.string().min(1),
        }),
        responses: {
            200: z.strictObject({
                uploadUrl: z.string().url(),
                blobPath: z.string(),
            }),
        },
    },
    fileNewClaim: {
        method: 'POST',
        path: '/file-new-claim/:id',
        pathParams: z.strictObject({
            id: z.string(),
        }),
        body: z.strictObject({}),
        responses: {
            200: z.strictObject({
                success: z.boolean(),
                validationErrors: z.record(z.string(), z.string()).nullable(),
            }),
        },
    },
    getFormSuggestions: {
        method: 'GET',
        path: '/get-form-suggestions',
        responses: {
            200: GetFormSuggestionsResponse,
        },
    },
    getContractNumberProposals: {
        method: 'GET',
        path: '/get-contract-number-proposals',
        query: GetContractNumberProposalsRequest,
        responses: {
            200: GetContractNumberProposalsResponse,
        },
    },
    getClaimLocationProposals: {
        method: 'GET',
        path: '/get-claim-location-proposals',
        query: GetClaimLocationProposalsRequest,
        responses: {
            200: GetClaimLocationProposalsResponse,
        },
    },
    getClaimTypeProposals: {
        method: 'GET',
        path: '/get-claim-type-proposals',
        query: GetClaimTypeProposalsRequest,
        responses: {
            200: GetClaimTypeProposalsResponse,
        },
    },
    getClaimCauseProposals: {
        method: 'GET',
        path: '/get-claim-cause-proposals',
        query: GetClaimCauseProposalsRequest,
        responses: {
            200: GetClaimCauseProposalsResponse,
        },
    },
}, { strictStatusCodes: true, pathPrefix: '/new-claim' });
